import translate from "@insite/client-framework/Translate";
import Link, { LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import applyPropBuilder from "@insite/mobius/utilities/applyPropBuilder";
import getColor from "@insite/mobius/utilities/getColor";
import { StyledProp } from "@insite/mobius/utilities/InjectableCss";
import injectCss from "@insite/mobius/utilities/injectCss";
import MobiusStyledComponentProps from "@insite/mobius/utilities/MobiusStyledComponentProps";
import { stripBrandPrefix, uppercaseToTitleCase } from "@tSurya/Common/Utilities/brandUtils";
import { isGlobalViewsWebsite, isMgbwWebsite } from "@tSurya/Common/Utilities/websiteUtils";
import * as React from "react";
import styled, { css, withTheme } from "styled-components";

export type BreadcrumbsProps = MobiusStyledComponentProps<
    "nav",
    {
        /** CSS string or styled-components function to be injected into this component.
         * @themable */
        css?: StyledProp<BreadcrumbsProps>;
        /** An array of link props */
        links?: LinkProps[];
        /**
         * Indicates how the `css` property is combined with the variant `css` property from the theme.
         * If true, the variant css is applied first and then the component css is applied after causing
         * a merge, much like normal CSS. If false, only the component css is applied, overriding the variant css in the theme.
         */
        mergeCss?: boolean;
        /**
         * Optional props to be passed down to all Link and Typography components.
         * Can be overriden by individual typographyProps in the links array.
         * @themable */
        typographyProps?: TypographyPresentationProps;
    }
>;

export type BreadcrumbsPresentationProps = Omit<BreadcrumbsProps, "links">;

export const Slash = styled(Typography as any).attrs({ children: "/" })`
    margin: 0 10px;
    display: block;
    color: ${getColor("text.accent")} !important;
`;

export const StyledNav = styled.nav.attrs({ "aria-label": "breadcrumbs" })`
    display: flex;
    flex-direction: row;
    align-items: center;
    ${injectCss}
`;

const Breadcrumbs: React.FC<BreadcrumbsProps> = withTheme(
    ({ links, mergeCss, isProductDetailsPage, productBrand, brandUrl, isProductListPage, pathName, ...otherProps }) => {
        const { spreadProps, applyStyledProp } = applyPropBuilder(otherProps, { component: "breadcrumbs" });
        const typographyProps = spreadProps("typographyProps");
        const resolvedMergeCss = mergeCss ?? otherProps?.theme?.breadcrumbs?.defaultProps?.mergeCss;
        const isGlobalViews = isGlobalViewsWebsite();
        const useMgbwProductListTitleCase = isProductListPage && isMgbwWebsite();
        const isBrandPath =
            pathName?.toLowerCase().includes("/brands") ||
            (isProductListPage &&
                links?.some(
                    (link: LinkProps) =>
                        typeof link.href === "string" && link.href.toLowerCase().startsWith("/brands"),
                ));
        // return null if `links` is an empty empty object, collection, map, or set.
        if (!links || links.length === 0) {
            return null;
        }

        const children = links.map((link: any, index: number) => {
            const linkCopy = {
                ...link,
            };

            let children = link.children;
            const isBrand = children === "Brands";
            const isShopAll = children === "Shop All Products";
            const isInvoiceDetails = children === "Invoice Details";
            if (isInvoiceDetails && typeof window !== "undefined") {
                const urlParams = new URLSearchParams(window?.location?.search);
                const invoiceNumber = urlParams.get("invoiceNumber");
                linkCopy.href = `/MyAccount/Invoices/Details?invoiceNumber=${invoiceNumber}`;
            }
            if (isBrand) {
                linkCopy.children = translate("Collections");
            }
            if (isShopAll) {
                linkCopy.children = "";
            }
            if (isBrandPath) {
                linkCopy.href = undefined;
                linkCopy.tabIndex = -1;
                linkCopy["aria-disabled"] = true;
            }

            if (isGlobalViews || isMgbwWebsite()) {
                if (typeof linkCopy.children === "string") {
                    linkCopy.children = stripBrandPrefix(linkCopy.children);
                }
                if (typeof children === "string") {
                    children = stripBrandPrefix(children);
                }
            }

            if (useMgbwProductListTitleCase) {
                if (typeof linkCopy.children === "string") {
                    linkCopy.children = uppercaseToTitleCase(linkCopy.children);
                }
                if (typeof children === "string") {
                    children = uppercaseToTitleCase(children);
                }
            }

            if (isProductDetailsPage && index === links.length - 1 && productBrand) {
                const brandText = isGlobalViews || isMgbwWebsite() ? stripBrandPrefix(productBrand) : productBrand;
                children = (
                    <>
                        <a href={`${brandUrl}/Catalog/All`}>{` ${brandText}`}</a>
                        <Typography
                            css={css`
                                color: ${getColor("text.accent")};
                                margin: 0 10px;
                                pointer-events: none;
                            `}
                        >
                            /
                        </Typography>
                        <span>{children}</span>
                    </>
                );
            }

            // yield null if no children
            if (!React.Children.count(children)) {
                return null;
            }
            const key = typeof children === "string" ? `${children}_${index}` : index;
            const displayText = children === "Shop All Products" ? "All" : children;
            if (index === links.length - 1) {
                return (
                    <Typography
                        mergeCss={resolvedMergeCss}
                        {...typographyProps}
                        aria-current="page"
                        key={key}
                        data-test-selector={`breadCrumb_${children}`}
                    >
                        {displayText}
                    </Typography>
                );
            }
            return (
                <React.Fragment key={key}>
                    <Link
                        typographyProps={typographyProps}
                        {...linkCopy}
                        className={
                            isBrandPath
                                ? `isNonClickable${index === links.length - 2 ? " mobileBreadcrumbLabel" : ""}`
                                : "isClickable"
                        }
                    />
                    <Slash
                        {...typographyProps}
                        className={isBrandPath ? "hide" : "display"}
                    />
                </React.Fragment>
            );
        });
        // return null if `links` map yields an array of nulls
        if (Object.keys(children.filter((x: typeof Link) => x !== null)).length === 0) {
            return null;
        }

        return (
            <StyledNav {...otherProps} css={applyStyledProp("css", resolvedMergeCss)}>
                {children}
            </StyledNav>
        );
    },
);

Breadcrumbs.displayName = "Breadcrumbs";
/** @component */
export default Breadcrumbs;
